import { ExternalLink, RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "../components/ui/Button";
import { Card, CardHint, CardTitle, CardValue } from "../components/ui/Card";
import { useConfirm } from "../components/ui/ConfirmDialog";
import { Skeleton } from "../components/ui/Feedback";
import { Select } from "../components/ui/Input";
import { useToast } from "../components/ui/Toast";
import { useSession } from "../features/auth/useSession";
import { useLocations } from "../features/locations/useLocations";
import {
	useSettings,
	useSwitchCheckoutPricing,
	useUpdateSettings,
} from "../features/settings/useSettings";
import { useStartSync, useSyncStatus } from "../features/sync/useSync";
import { cn } from "../lib/cn";
import { formatDate, formatNumber, titleCase } from "../lib/format";
import { listTimeZones } from "../lib/timezones";
import type { CheckoutPricing, SyncJob, SyncKind } from "../lib/types";

const SYNC_KINDS: { value: SyncKind; label: string }[] = [
	{ value: "products", label: "Products" },
	{ value: "inventory", label: "Inventory" },
	{ value: "selling-plans", label: "Subscriptions" },
	{ value: "collections", label: "Collections" },
];

const CHECKOUT_PRICING: Record<
	CheckoutPricing,
	{ label: string; summary: string; title: string; confirm: string }
> = {
	discount: {
		label: "Discount",
		title: "Price gifts with discounts?",
		summary:
			"Each campaign is an automatic discount. Your discount codes only stack with gifts when they are set to combine with product discounts.",
		confirm:
			"Every live campaign goes back to being its own discount, and each campaign's Combines with settings apply again.",
	},
	cart_transform: {
		label: "Cart price (Shopify Plus)",
		title: "Price gifts in the cart?",
		summary:
			"Gifts are priced in the cart before any discount runs, so every discount code stacks with them. No code can be kept from stacking, and the cart shows the reduced price rather than a discount line.",
		confirm:
			"Your AOVtools discounts are removed and every live campaign is priced in the cart instead. Every discount code will stack with gifts.",
	},
};

const PRICING_MODES: readonly CheckoutPricing[] = ["discount", "cart_transform"];

const SyncCard = ({ title, job }: { title: string; job: SyncJob | null }) => (
	<Card>
		<CardTitle>{title}</CardTitle>
		<CardValue>{job ? titleCase(job.status) : "Never run"}</CardValue>
		<CardHint>
			{job
				? `${formatNumber(job.rowsImported)} imported · ${formatDate(job.finishedAt)}`
				: "No sync has been recorded yet"}
		</CardHint>
		{job?.error ? <CardHint className="text-ink">{job.error}</CardHint> : null}
	</Card>
);

const Section = ({
	title,
	description,
	children,
}: {
	title: string;
	description: string;
	children: ReactNode;
}) => (
	<section className="space-y-3">
		<div>
			<h3 className="text-sm font-medium text-ink">{title}</h3>
			<p className="mt-0.5 text-xs text-ink-muted">{description}</p>
		</div>
		{children}
	</section>
);

export const SettingsPage = () => {
	const session = useSession();
	const sync = useSyncStatus();
	const locations = useLocations();
	const settings = useSettings();
	const updateSettings = useUpdateSettings();
	const switchPricing = useSwitchCheckoutPricing();
	const confirm = useConfirm();
	const startSync = useStartSync();
	const toast = useToast();
	const [syncKind, setSyncKind] = useState<SyncKind>("products");

	const save = (patch: Parameters<typeof updateSettings.mutate>[0], label: string) =>
		updateSettings.mutate(patch, {
			onSuccess: () => toast.push(`${label} saved`),
			onError: (error) => toast.push(error.message, "error"),
		});

	const choosePricing = (mode: CheckoutPricing) =>
		confirm({
			title: CHECKOUT_PRICING[mode].title,
			description: CHECKOUT_PRICING[mode].confirm,
			confirmLabel: "Switch",
			onConfirm: () =>
				switchPricing.mutate(mode, {
					onSuccess: () => toast.push("Checkout pricing switched"),
					onError: (error) => toast.push(error.message, "error"),
				}),
		});

	const runSync = () =>
		startSync.mutate(syncKind, {
			onSuccess: () => toast.push("Sync queued"),
			onError: (error) => toast.push(error.message, "error"),
		});

	const loading = settings.isPending || locations.isPending;

	return (
		<div className="max-w-3xl space-y-10">
			<div>
				<h2 className="text-xl font-semibold tracking-tight text-ink">Settings</h2>
				<p className="mt-1 text-sm text-ink-muted">Store configuration and catalog sync.</p>
			</div>

			<Section
				title="Storefront content"
				description="Campaign content only appears once the AOVtools app embed is switched on in your theme."
			>
				{session.data ? (
					<a
						href={`https://${session.data.tenant.shopDomain}/admin/themes/current/editor?context=apps`}
						target="_blank"
						rel="noreferrer"
						className="inline-flex"
					>
						<Button variant="outline">
							<ExternalLink className="h-4 w-4" strokeWidth={1.75} />
							Open theme editor
						</Button>
					</a>
				) : (
					<Skeleton className="h-9 w-48" />
				)}
				<p className="text-xs text-ink-muted">
					In the editor, open <span className="text-ink">App embeds</span> and enable{" "}
					<span className="text-ink">AOVtools Content</span>. This only has to be done once per
					theme.
				</p>
			</Section>

			<Section
				title="Primary inventory location"
				description="Gift availability and backup fallbacks are computed against stock here."
			>
				{loading ? (
					<Skeleton className="h-9 w-full max-w-sm" />
				) : (
					<Select
						value={settings.data?.settings.primaryLocationId ?? ""}
						aria-label="Primary inventory location"
						disabled={updateSettings.isPending}
						onChange={(event) =>
							save({ primaryLocationId: event.target.value || null }, "Primary location")
						}
						className="max-w-sm"
					>
						<option value="">No location selected</option>
						{locations.data?.items.map((location) => (
							<option key={location.id} value={location.id}>
								{location.name ?? "Unnamed location"}
								{location.isActive ? "" : " (inactive)"}
							</option>
						))}
					</Select>
				)}
			</Section>

			<Section
				title="Time zone"
				description="Campaign start and end times are interpreted in this zone. Defaults to your Shopify setting."
			>
				{loading ? (
					<Skeleton className="h-9 w-full max-w-sm" />
				) : (
					<Select
						value={settings.data?.settings.timezone ?? ""}
						aria-label="Time zone"
						disabled={updateSettings.isPending}
						onChange={(event) => save({ timezone: event.target.value || null }, "Time zone")}
						className="max-w-sm"
					>
						<option value="">Not set</option>
						{listTimeZones().map((zone) => (
							<option key={zone} value={zone}>
								{zone}
							</option>
						))}
					</Select>
				)}
			</Section>

			<Section
				title="Checkout pricing"
				description="How campaign gifts and discounts reach checkout. Switching moves every live campaign at once."
			>
				{loading ? (
					<Skeleton className="h-9 w-full max-w-sm" />
				) : (
					<>
						<Select
							value={settings.data?.settings.checkoutPricing ?? "discount"}
							aria-label="Checkout pricing"
							disabled={switchPricing.isPending}
							onChange={(event) => {
								const mode = PRICING_MODES.find((option) => option === event.target.value);
								if (mode) choosePricing(mode);
							}}
							className="max-w-sm"
						>
							{PRICING_MODES.map((mode) => (
								<option key={mode} value={mode}>
									{CHECKOUT_PRICING[mode].label}
								</option>
							))}
						</Select>
						<p className="text-xs text-ink-muted">
							{switchPricing.isPending
								? "Switching…"
								: CHECKOUT_PRICING[settings.data?.settings.checkoutPricing ?? "discount"].summary}
						</p>
					</>
				)}
			</Section>

			<Section
				title="Catalog sync"
				description="Running products refreshes inventory, subscriptions and collections with it."
			>
				<div className="flex flex-wrap gap-2">
					<Select
						value={syncKind}
						aria-label="Sync to run"
						onChange={(event) => setSyncKind(event.target.value as SyncKind)}
						className="max-w-xs"
					>
						{SYNC_KINDS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
					<Button onClick={runSync} disabled={startSync.isPending}>
						<RefreshCw
							className={cn("h-4 w-4", startSync.isPending && "animate-spin")}
							strokeWidth={2}
						/>
						{startSync.isPending ? "Queueing…" : "Run sync"}
					</Button>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					{sync.isPending ? (
						<>
							<Skeleton className="h-36" />
							<Skeleton className="h-36" />
						</>
					) : (
						<>
							<SyncCard title="Product sync" job={sync.data?.products ?? null} />
							<SyncCard title="Inventory sync" job={sync.data?.inventory ?? null} />
							<SyncCard title="Subscription sync" job={sync.data?.sellingPlans ?? null} />
							<SyncCard title="Collection sync" job={sync.data?.collections ?? null} />
						</>
					)}
				</div>
			</Section>
		</div>
	);
};
